import {isEmpty} from 'lodash';
import styled, {withTheme} from 'styled-components';

import sideMenuIconUrl from '~/assets/images/icons/table_report_menu_icon.svg';
import ClickOutsideDiv from '~/shared/components/ClickOutsideDiv';
import {
  flexCenterHorizontally,
  flexColumnCenterVertically,
  FlexColumnCenterVertically,
} from '~/shared/theme/FlexLayout';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import {getLocalizationService} from '~/shared/services/localisationService';
import Button from '~/shared/components/Button';

export const ClickOutsideWithExtraProps = ({isMobile: _ignore, ...props}) => <ClickOutsideDiv {...props} />;

export const Menu = styled(ClickOutsideWithExtraProps)`
  max-height: 176px;
  min-width: 160px;
  background-color: ${({theme}) => theme.userReport.transactionTable.menu.backgroundColor};
  box-shadow: ${({theme}) => theme.shadows.shadow2};
  z-index: 2;
  padding: 8px 0;
  position: absolute;
  border-radius: 8px;
  ${flipOnLTR`left: 18px;`}
  top: 0;
  ${({isMobile}) =>
    isMobile &&
    `
      min-width: 100%;
      left: 0;
      top: 40px;
    `}
`;

export const Wrapper = styled(FlexColumnCenterVertically)`
  position: absolute;
  ${flipOnLTR`left: 0;`}
  top: 0;
  width: 30px;
  height: 100%;
  z-index: 10;
  ${({isMobile}) =>
    isMobile &&
    `
    top: auto;
    position: initial;
    img {
     transform: rotate(90deg);
    }
  `}
`;

export const MenuButton = styled(Button).attrs({
  role: 'button',
})`
  color: ${({theme}) => theme.userReport.transactionTable.menu.text};
  width: 100%;
  cursor: pointer;
  height: 40px;
  ${flexColumnCenterVertically};
  padding: 0 10px;
  align-items: flex-start;

  &:hover,
  &:focus {
    background-color: ${({theme}) => theme.colors.actionWhiteHover};
  }

  ${({isMobile, theme}) =>
    isMobile &&
    `
      color: ${theme.userReport.transactionTable.menu.text};
      font-weight: normal;
    `}
`;

export const SideMenuButton = styled(Button).attrs({
  role: 'button',
  type: 'button',
})`
  ${flexCenterHorizontally};
  height: 100%;
  align-items: center;
`;

export const SideMenuIcon = withTheme(({theme}) => (
  <svg width="3px" height="15px" viewBox="0 0 3 15" version="1.1" xmlns="http://www.w3.org/2000/svg">
    <g id="Symbols" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <g transform="translate(-16.000000, -13.000000)" fill={theme.colors.surfacePrimaryAction}>
        <g>
          <g transform="translate(16.000000, 13.000000)">
            <g>
              <circle cx="1.5" cy="1.5" r="1.5" />
              <circle cx="1.5" cy="7.5" r="1.5" />
              <circle cx="1.5" cy="13.5" r="1.5" />
            </g>
          </g>
        </g>
      </g>
    </g>
  </svg>
));

export const StyledSideMenuIcon = styled(SideMenuIcon).attrs(() => ({
  src: sideMenuIconUrl,
}))`
  width: 30px;
  height: 15px;
  cursor: pointer;
  padding: 0 8px;
`;

const SideMenu = ({order, menuList, isMobile, onClick, openedRowMenuId}) => {
  const {t} = getLocalizationService();

  const menuIsOpen = openedRowMenuId === order?.id;

  const prevent = async event => {
    event.preventDefault?.();
    event.stopPropagation?.();
  };

  const handleOnClick = e => {
    prevent(e);
    onClick?.(order?.id);
  };

  const handleOnBlur = e => {
    prevent(e);
    onClick?.(null);
  };

  const handleClickoutside = e => {
    handleOnBlur(e);
    if (e.target.type === 'button' && e.target.id === 'side-menu-button') {
      e.target.focus();
    }
  };

  const handleItemClick = (e, itemClickFunc) => {
    e.stopPropagation();
    handleClickoutside(e);
    itemClickFunc?.(order);
  };

  if (isEmpty(menuList)) {
    return null;
  }

  return (
    <Wrapper isMobile={isMobile} onClick={prevent}>
      <SideMenuButton
        id="side-menu-button"
        data-test-id="sideMenuBtn"
        isOpen={menuIsOpen}
        onFocus={handleOnClick}
        onMouseDown={handleOnClick}
        isExpandable
      >
        <StyledSideMenuIcon alt={t('menu')} />
      </SideMenuButton>
      {menuIsOpen && (
        <Menu isMobile={isMobile} onClickOutside={handleClickoutside}>
          {menuList.map(({name, onClick: itemClick}) => (
            <MenuButton key={name} data-test-id={`sideMenuItem-${name}`} isMobile={isMobile} onClick={e => handleItemClick(e, itemClick)}>
              {t(name)}
            </MenuButton>
          ))}
        </Menu>
      )}
    </Wrapper>
  );
};

export default SideMenu;
